/**
 * Bundle Builder Product Card
 *
 * Handles interactions for bundle builder product cards including:
 * - Color swatch changes (image updates, variant selection)
 * - Add to bag functionality
 * - Quantity selector controls (+, -, direct input)
 * - Custom event communication with bundle builder
 *
 * Uses event delegation to avoid duplicate handlers.
 */

;(function () {
  'use strict'

  let initialized = false

  /**
   * Initialize bundle product card event delegation
   * Only runs once - uses event delegation so new cards work automatically
   */
  function initBundleProductCards() {
    if (initialized) return
    initialized = true

    // Event delegation for all bundle card interactions
    document.addEventListener('click', handleBundleCardClick)
    document.addEventListener('change', handleBundleCardChange)
  }

  window.initBundleProductCards = initBundleProductCards

  /**
   * Handle click events on bundle cards (ADD button, +/- buttons)
   */
  function handleBundleCardClick(e) {
    const target = e.target

    // ADD button
    if (target.closest('.bundle-card__add-btn')) {
      e.preventDefault()
      e.stopPropagation()
      const btn = target.closest('.bundle-card__add-btn')
      const card = btn.closest('.bundle-card')
      if (!card) return

      const variantId = card.dataset.variantId
      const price = card.dataset.price
      const compareAtPrice = card.dataset.compareAtPrice || price

      dispatchCartUpdateEvent(card, 1)

      if (window.BundleBuilder && window.BundleBuilder.updateBundleCardActions) {
        window.BundleBuilder.updateBundleCardActions(card, 1, price, variantId, compareAtPrice)
      }
      return
    }

    // Increase button
    if (target.closest('.bundle-card__qty-increase')) {
      e.preventDefault()
      e.stopPropagation()
      const btn = target.closest('.bundle-card__qty-increase')
      const card = btn.closest('.bundle-card')
      if (!card) return

      const qtyInput = card.querySelector('.bundle-card__qty-input')
      if (!qtyInput) return

      let currentQty = parseInt(qtyInput.value) || 0
      const maxQty = parseInt(qtyInput.max) || 999

      if (currentQty < maxQty) {
        currentQty++
        qtyInput.value = currentQty
        dispatchCartUpdateEvent(card, currentQty)
      }
      return
    }

    // Decrease button
    if (target.closest('.bundle-card__qty-decrease')) {
      e.preventDefault()
      e.stopPropagation()
      const btn = target.closest('.bundle-card__qty-decrease')
      const card = btn.closest('.bundle-card')
      if (!card) return

      const qtyInput = card.querySelector('.bundle-card__qty-input')
      if (!qtyInput) return

      let currentQty = parseInt(qtyInput.value) || 0

      if (currentQty > 0) {
        currentQty--
        qtyInput.value = currentQty
        dispatchCartUpdateEvent(card, currentQty)

        // If quantity goes to 0, switch back to ADD button
        if (currentQty === 0) {
          const variantId = card.dataset.variantId
          const price = card.dataset.price
          const compareAtPrice = card.dataset.compareAtPrice || price
          if (window.BundleBuilder && window.BundleBuilder.updateBundleCardActions) {
            window.BundleBuilder.updateBundleCardActions(card, 0, price, variantId, compareAtPrice)
          }
        }
      }
      return
    }
  }

  /**
   * Handle change events on bundle cards (color swatches, qty input)
   */
  function handleBundleCardChange(e) {
    const target = e.target

    // Color swatch change
    if (target.matches('.bundle-card .js-color-swatches input[type="radio"]')) {
      const card = target.closest('.bundle-card')
      if (!card || !target.checked) return

      const productHandle = card.dataset.product
      const currentColor = target.value
      const currentVariantId = target.dataset.variantId
      const variantImageUrl = target.dataset.variantImage

      // Quick image switch
      const primaryImage = card.querySelector('.media--first')
      if (variantImageUrl && primaryImage && variantImageUrl !== primaryImage.src) {
        const newImage = new Image()
        newImage.className = primaryImage.className
        newImage.alt = primaryImage.alt
        newImage.src = variantImageUrl
        newImage.onload = () => {
          primaryImage.parentNode.replaceChild(newImage, primaryImage)
        }
      }

      // Fetch full product data
      fetch(`${window.Shopify.routes.root}products/${productHandle}.js`)
        .then((response) => response.json())
        .then((product) => {
          const variant = product.variants.find(
            (item) =>
              (currentVariantId &&
                item.id == currentVariantId &&
                item.featured_media != null &&
                item.options.includes(currentColor)) ||
              (!currentVariantId && item.featured_media != null && item.options.includes(currentColor))
          )

          if (variant) {
            updateCardVariantData(card, variant)
            updateAddToBagButton(card, variant)

            if (window.BundleBuilder && window.BundleBuilder.syncQuantitySelectorWithState) {
              window.BundleBuilder.syncQuantitySelectorWithState(String(variant.id), card)
            }

            // Update image with transition (non-critical, wrapped in try-catch)
            try {
              const currentImage = card.querySelector('.media--first')
              if (variant.featured_media && variant.featured_media.src && currentImage) {
                const newPrimaryImage = createImageElement(
                  variant.featured_media,
                  currentImage.className,
                  currentImage.sizes,
                  product.title
                )

                if (newPrimaryImage.src && newPrimaryImage.src !== currentImage.src) {
                  currentImage.style.transition = 'opacity 200ms ease-in-out'
                  currentImage.style.opacity = '0'
                  setTimeout(() => {
                    currentImage.replaceWith(newPrimaryImage)
                    newPrimaryImage.style.opacity = '0'
                    newPrimaryImage.style.transition = 'opacity 200ms ease-in-out'
                    newPrimaryImage.offsetHeight
                    newPrimaryImage.style.opacity = '1'
                  }, 200)
                }
              }
            } catch (imgError) {
              console.warn('Image update failed:', imgError)
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching product data:', error)
        })
      return
    }

    // Quantity input direct change
    if (target.matches('.bundle-card__qty-input')) {
      const card = target.closest('.bundle-card')
      if (!card) return

      let newQty = parseInt(target.value) || 0
      const minQty = parseInt(target.min) || 0
      const maxQty = parseInt(target.max) || 999

      if (newQty < minQty) newQty = minQty
      if (newQty > maxQty) newQty = maxQty

      target.value = newQty
      dispatchCartUpdateEvent(card, newQty)

      const variantId = card.dataset.variantId
      const price = card.dataset.price
      const compareAtPrice = card.dataset.compareAtPrice || price
      if (window.BundleBuilder && window.BundleBuilder.updateBundleCardActions) {
        window.BundleBuilder.updateBundleCardActions(card, newQty, price, variantId, compareAtPrice)
      }
      return
    }
  }

  /**
   * Update card data attributes with variant info
   */
  function updateCardVariantData(card, variant) {
    let price = variant.price
    let compareAtPrice = variant.compare_at_price || variant.price

    if (window.BundleBuilder && window.BundleBuilder.getAdjustedPrice) {
      const adjusted = window.BundleBuilder.getAdjustedPrice(String(variant.id), variant.price)
      price = adjusted.price
      compareAtPrice = adjusted.compareAtPrice || compareAtPrice
    }

    card.dataset.variantId = variant.id
    card.dataset.price = price
    card.dataset.compareAtPrice = compareAtPrice
    card.dataset.variantTitle = variant.title
    if (variant.featured_media) {
      card.dataset.variantImage = variant.featured_media.src
    }
  }

  /**
   * Update price display and data attributes for new variant
   * Note: Button state is handled by syncQuantitySelectorWithState which checks cart
   */
  function updateAddToBagButton(card, variant) {
    const priceElement = card.querySelector('.card__title.price')

    let price = variant.price
    let compareAtPrice = variant.compare_at_price || variant.price

    if (window.BundleBuilder && window.BundleBuilder.getAdjustedPrice) {
      const adjusted = window.BundleBuilder.getAdjustedPrice(String(variant.id), variant.price)
      price = adjusted.price
      compareAtPrice = adjusted.compareAtPrice || compareAtPrice
    }

    card.dataset.variantId = variant.id
    card.dataset.price = price
    card.dataset.compareAtPrice = compareAtPrice
    card.dataset.variantTitle = variant.title

    if (priceElement) {
      priceElement.textContent = formatMoney(price)
    }
    // Button/quantity state is synced by syncQuantitySelectorWithState after this
  }

  /**
   * Dispatch cart update event
   */
  function dispatchCartUpdateEvent(card, quantity) {
    const event = new CustomEvent('bundle:cartUpdate', {
      bubbles: true,
      detail: {
        variantId: card.dataset.variantId,
        quantity: quantity,
        productHandle: card.dataset.productHandle || '',
        cardElement: card,
      },
    })
    card.dispatchEvent(event)
  }

  /**
   * Create image element from Shopify media
   */
  function createImageElement(media, className, sizes, altText) {
    const img = document.createElement('img')
    img.className = className
    img.alt = altText || ''
    img.sizes = sizes || '100vw'
    img.loading = 'lazy'

    // Guard against missing src
    if (!media || !media.src) {
      return img
    }

    const widths = [360, 535, 720, 940, 1066, 1200, 1400, 1600]
    const srcset = widths
      .map((width) => {
        const url = media.src.replace(/\.jpg|\.png|\.gif/, `_${width}x$&`)
        return `${url} ${width}w`
      })
      .join(', ')

    img.srcset = srcset
    img.src = media.src
    return img
  }

  /**
   * Format money
   */
  function formatMoney(cents) {
    if (typeof cents !== 'number') {
      cents = parseInt(cents) || 0
    }
    const dollars = (cents / 100).toFixed(2).replace(/\.?0+$/, '')
    if (window.Shopify && window.Shopify.formatMoney) {
      const formatted = window.Shopify.formatMoney(cents, window.theme.moneyFormat || '${{amount}}')
      return formatted.replace(/\.?0+$/, '')
    }
    return `$${dollars}`
  }

  /**
   * Set max quantity for all cards
   */
  window.setBundleCardMaxQuantity = function (maxQty) {
    document.querySelectorAll('.bundle-card__qty-input').forEach((input) => {
      input.max = maxQty
    })
  }

  /**
   * Reset a card quantity to 0
   */
  window.resetBundleCard = function (variantId) {
    const card = document.querySelector(`.bundle-card[data-variant-id="${variantId}"]`)
    if (!card) return
    const qtyInput = card.querySelector('.bundle-card__qty-input')
    if (qtyInput) {
      qtyInput.value = '0'
    }
  }
})()
